import "./form-quotation.scss";

import { QuotationFormSchema } from "@factory/form/form.rule";
import { useQyGetPackage } from "@factory/query/package.query";
import { useQyGetPackageAccommodation } from "@factory/query/package-accommodation.query";
import { useQyGetPackageActivity } from "@factory/query/package-activity.query";
import { useQyGetPackageTransport } from "@factory/query/package-transport.query";
import { useQyGetStatus } from "@factory/query/status.query";
import { LabelValue } from "@shared/models/label-value.interface";
import DropdownControl from "@shared/ui/hook-form/dropdown-control/dropdown-control";
import InputControl from "@shared/ui/hook-form/input-control/input-control";
import InputDateControl from "@shared/ui/hook-form/input-date-control/input-date-control";
import InputDigitControl from "@shared/ui/hook-form/input-digit-control/input-digit-control";
import { useFormContext } from "react-hook-form";

import useAutocompleteAgent from "../../agent/autocomplete-agent/autocomplete-agent";
import { useAutocompleteClient } from "../../client/autocomplete-client/autocomplete-client";
import useAutocompleteLocation from "../../location/autocomplete-location/autocomplete-location";

type InputFormSchema = QuotationFormSchema;
interface FormQuotationProps {
  isEditing?: boolean;
}
export function FormQuotation({ isEditing = false }: FormQuotationProps) {
  const { control, setValue, getValues, watch } =
    useFormContext<QuotationFormSchema>();
  const packageCode = watch("basePackage");

  // THIS IS THE LOCAL QUERY DECLARATION
  useQyGetPackageActivity(
    "",
    9999,
    0,
    undefined,
    { package: packageCode || "" },
    true,
    (response) => {
      if (!response?.data) {
        return;
      }
      if (!isEditing) {
        const activities = (response?.data || []).map(
          (item) =>
            ({
              ...item,
              baseActivity: item.activity,
              name: item.activity_name,
              description: item.activity_description,
              isChildActivity: item.activity_is_child_activity,
            }) as any,
        );
        setValue("activities", activities);
      }
    },
  );
  useQyGetPackageTransport(
    "",
    9999,
    0,
    undefined,
    { package: packageCode || "" },
    true,
    (response) => {
      if (!response?.data) {
        return;
      }
      if (!isEditing) {
        const transport = (response?.data || []).map(
          (item) =>
            ({
              ...item,
              transportType: item.transport_type,
              transportTypeName: item.transport_type_name,
              fromLocation: item.from_location,
              fromLocationName: item.from_location_name,
              toLocation: item.to_location,
              toLocationName: item.to_location_name,
              pricePerPax: item.price_per_pax,
              numPeople: item.num_people,
              unit: item.unit,
              amount: item.amount,
            }) as any,
        );
        setValue("transport", transport);
      }
    },
  );
  useQyGetPackageAccommodation(
    "",
    9999,
    0,
    undefined,
    { package: packageCode || "" },
    true,
    (response) => {
      if (!response?.data) {
        return;
      }
      if (!isEditing) {
        const accommodation = (response?.data || []).map(
          (item) =>
            ({
              ...item,
              hotel: item.hotel,
              hotelName: item.hotel_name,
              roomType: item.room_type,
              roomTypeName: item.room_type_name,
              withBreakfast: item.hotel_with_breakfast,
              price: item.price,
              numPeople: item.num_people,
            }) as any,
        );
        setValue("accommodation", accommodation);
      }
    },
  );

  const statusResponse = useQyGetStatus("");
  const statusData = statusResponse?.data?.data || [];
  const statusOptions = LabelValue.MapArray(
    statusData,
    (item) => item.description,
    (item) => item.code,
  );

  const { formElement: autocompleteLocation } = useAutocompleteLocation(
    getValues("location") || "",
    "location",
    "Destination",
    "",
    (selectedLocation) => {
      if (selectedLocation) {
        setValue("location", selectedLocation.value);
        setValue("locationName", selectedLocation.label);
      } else {
        setValue("location", "");
        setValue("locationName", "");
      }
    },
  );
  // const { formElement: autocompletePackage } = useAutocompletePackage(
  //   getValues("basePackage") || "",
  //   "basePackage",
  //   "Base Package",
  //   "",
  //   (selectedPackage) => {
  //     if (selectedPackage) {
  //       setValue("basePackage", selectedPackage.value);
  //       setValue("packageName", selectedPackage.label);
  //     } else {
  //       setValue("basePackage", "");
  //       setValue("packageName", "");
  //     }
  //   }
  // );
  const { data: packagesResponse } = useQyGetPackage("");
  const packagesData = packagesResponse?.data || [];
  const packageOptions = LabelValue.MapArray(
    packagesData,
    (item) => item.name,
    (item) => item.code,
  );

  const { formElement: autocompleteClient } = useAutocompleteClient(
    getValues("client") || "",
    "client",
    "Client",
    "",
    (selectedClient) => {
      if (selectedClient) {
        setValue("client", selectedClient.value);
        setValue("clientName", selectedClient.label);
      } else {
        setValue("client", "");
        setValue("clientName", "");
      }
    },
  );

  const { formElement: autocompleteAgent } = useAutocompleteAgent(
    getValues("agent") || "",
    "agent",
    "Agent",
    "",
    (selectedAgent) => {
      if (selectedAgent) {
        setValue("agent", selectedAgent.value);
        setValue("agentMarkup", selectedAgent.payload);
      } else {
        setValue("agent", "");
        setValue("agentMarkup", 0);
      }
    },
  );
  // {autocompletePackage}

  return (
    <div
      id="FormQuotation"
      className="quotation grid grid-cols-1 md:grid-cols-2"
    >
      <div className="space-y-8">
        <DropdownControl<InputFormSchema>
          options={statusOptions}
          control={control}
          name="status"
          label="Status"
          className="w-full md:w-3/4"
          containerClassName="pb-2"
          placeholder="Select Status"
        />
        <DropdownControl<InputFormSchema>
          options={packageOptions}
          control={control}
          name="basePackage"
          label="Base Package"
          className="w-full md:w-3/4"
          containerClassName="pb-2"
          placeholder="Select Base Package"
          onSelect={(value) => {
            const selectedPackage = packageOptions.filter(
              (item) => item.value === value,
            );
            setValue("packageName", selectedPackage?.[0]?.label || "");
            setValue("basePackageName", selectedPackage?.[0]?.label || "");
          }}
        />
        <InputControl<InputFormSchema>
          control={control}
          name="packageName"
          label="Custom Package Name"
          className="w-full md:w-3/4"
          containerClassName="pb-2"
          placeholder="Enter packageName"
        />
        {autocompleteClient}
        {autocompleteLocation}
        {autocompleteAgent}
        <InputDigitControl<InputFormSchema>
          control={control}
          name="agentMarkup"
          label="Agent Markup (%)"
          className="w-full md:w-3/4"
          containerClassName="pb-2"
          placeholder="Enter agentMarkup"
        />
      </div>
      <div>
        <div className="grid grid-cols-1 md:grid-cols-2">
          <InputDateControl<InputFormSchema>
            control={control}
            name="startDate"
            label="Start Date"
            className="w-full md:w-3/4"
            containerClassName="pb-2"
            placeholder="Enter startDate"
          />
          <InputDateControl<InputFormSchema>
            control={control}
            name="endDate"
            label="End Date"
            className="w-full md:w-3/4"
            containerClassName="pb-2"
            placeholder="Enter endDate"
          />
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2">
          <InputDigitControl<InputFormSchema>
            control={control}
            name="numAdults"
            label="Adults"
            inputClassName="w-full"
            containerClassName="pb-2"
            placeholder="Enter number of adults"
          />
          <InputDigitControl<InputFormSchema>
            control={control}
            name="numChildren"
            label="Children"
            inputClassName="w-full"
            containerClassName="pb-2"
            placeholder="Enter number of children"
          />
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2">
          <InputDigitControl<InputFormSchema>
            control={control}
            name="numDays"
            label="Days"
            inputClassName="w-full"
            containerClassName="pb-2"
            placeholder="Enter number of days"
          />
          <InputDigitControl<InputFormSchema>
            control={control}
            name="numNights"
            label="Nights"
            inputClassName="w-full"
            containerClassName="pb-2"
            placeholder="Enter number of nights"
          />
        </div>
      </div>
    </div>
  );
}

export default FormQuotation;
